import * as React from 'react';
import { useState, useEffect } from 'react';
import Avatar from '@mui/material/Avatar';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { useParams, useNavigate } from 'react-router-dom';
import { Container, Box, Typography, TextField, Button } from '@mui/material';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE } from '../../common/AppConstants';
import { Helmet } from 'react-helmet';
import logger from '../../logger';

export default function PasswordReset() {
  const { addNotification } = useNotification();
  // Capture uid and token from the URL
  const { uid, token } = useParams();
  const navigate = useNavigate();

  // Local state for form values and messages
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isResetLoading, setIsResetLoading] = useState(false);
  const [isResetDone, setIsResetDone] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const resetAllErrors = () => {
    setEditErrors({});
  };

  useEffect(() => {
    resetAllErrors();
  }, []);

  const handleChange = (fieldAttr, value) => {
    // Clear error when user edits the field
    setEditErrors((prevErrors) => ({
      ...prevErrors,
      [fieldAttr]: undefined,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Client-side check: passwords must match
    if (newPassword !== confirmPassword) {
      setEditErrors((prev) => ({
        ...prev,
        confirm_password: ["Passwords don't match"],
      }));
      return;
    }

    resetAllErrors();
    setIsResetLoading(true); // Disable button

    const data = {
      uid: uid,
      token: token,
      new_password: newPassword,
    };
    
    await httpRequest({
      method: 'POST',
      url: 'user/password-reset/',
      data: data,
      onSuccess: (data) => {
        setIsResetDone(true); // Disable button
        // Redirect after a brief delay (e.g., 3 seconds)
        setTimeout(() => {
          navigate('/sign-in');
        }, 3000);
      },
      onFailure: (data) => {
        setEditErrors(data);
      },
      addNotification,
    });

    setIsResetLoading(false);
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Container component="main" maxWidth="xs">
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: 2,
          }}
        >
          <Avatar sx={{ m: 1, bgcolor: 'secondary.main' }}>
            <LockOutlinedIcon />
          </Avatar>
          <Typography variant="h1">Reset Your Password</Typography>
          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{ mt: 1 }}
          >
            <TextField
              margin="normal"
              required
              fullWidth
              name="new_password"
              label="New Password"
              type="password"
              id="new_password"
              value={newPassword}
              autoFocus
              onChange={(e) => {
                setNewPassword(e.target.value);
                handleChange('new_password', e.target.value);
              }}
              error={Boolean(editErrors.new_password)}
              helperText={editErrors.new_password?.[0] || ''}
              disabled={isResetDone}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm_password"
              label="Confirm Password"
              type="password"
              id="confirm_password"
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                handleChange('confirm_password', e.target.value);
              }}
              error={Boolean(editErrors.confirm_password)}
              helperText={editErrors.confirm_password?.[0] || ''}
              disabled={isResetDone}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ mt: 3, mb: 2 }}
              disabled={isResetLoading || isResetDone}
            >
              Reset Password
              {isResetLoading ? 'Resetting' : 'Reset Password'}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
}
